// src/pages/profile/ProfileAvatar.jsx
import { useRef, useState } from "react";
import { Upload, message, Button, Tooltip, Progress, Image } from "antd";
import {
  CameraOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import classes from "./ProfileAvatar.module.scss";

export const ProfileAvatar = ({ user, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState(0);
  const abortRef = useRef(null);
  const beforeUpload = (file) => {
    const isImage = /^image\/(png|jpe?g|webp|gif|avif)$/.test(file.type);
    if (!isImage) message.error("Only images are allowed (jpg/png/webp/gif)");
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) message.error("Image must be smaller than 5MB");
    return isImage && isLt5M;
  };

  // OPTION A: upload to your backend (which forwards to S3 with multer-s3)
  // expects: POST routes.uploadImage  -> returns { url: "https://s3…/image.jpg" }
  // then PATCH user with { image: url }
  const customRequest = async ({ file, onSuccess, onError }) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setPercent(0);
    try {
      const form = new FormData();
      form.append("image", file);

      // hit your existing upload endpoint (adjust if different)
      const { data } = await axios.post(routes.uploadImage, form, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const raw = Math.round((evt.loaded / evt.total) * 100);
          const shown = Math.min(raw, 95); // <-- cap at 95
          setPercent(shown);
        },
      });

      const url = data?.url || data?.location || data?.image || "";
      if (!url) throw new Error("Upload failed: no URL returned");

      // save on user profile
      await axios.put(routes.updateUser, { image: url[0] });

      setPercent(100);
      onSuccess?.({ url });
      message.success("Profile photo updated");
      onUpdated?.(url);
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.message || "Upload failed");
      onError?.(e);
    } finally {
      setLoading(false);
    }
  };

  const cancelUpload = () => {
    abortRef.current?.abort();
    setLoading(false);
  };

  return (
    <>
      {loading && (
        <div className={classes.uploading}>
          <span style={{ fontSize: 23 }}> Uploading Photo ...</span>
          <Progress
            percent={percent}
            status="active"
            trailColor="white"
            strokeColor={"#fe643f"}
            percentPosition={{ align: "center", type: "inner" }}
            size={[400, 20]}
            style={{ marginTop: "2rem" }}
          />
          <Button
            type="primary"
            icon={<CloseOutlined />}
            onClick={cancelUpload}
            style={{ padding: "1rem 2rem", marginTop: "2rem" }}
          >
            Cancel
          </Button>
        </div>
      )}
      <div className={classes.avatarWrap}>
        <div className={classes.avatarImg}>
          <Image
            src={user?.image || "/images/avatar-placeholder.png"}
            alt={user?.fullName || "User"}
            preview
          />
        </div>

        <div className={classes.overlay}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={beforeUpload}
            customRequest={customRequest}
          >
            <Tooltip title="Change photo">
              <Button
                type="primary"
                icon={loading ? <LoadingOutlined /> : <CameraOutlined />}
                disabled={loading}
                style={{ borderRadius: "50%" }}
              >
                {/* {loading ? "Uploading…" : "Change "} */}
              </Button>
            </Tooltip>
          </Upload>
        </div>
      </div>
    </>
  );
};
export default ProfileAvatar;
